import React, { useState } from "react";

import { SingleValue } from "react-select";
import { SendMessage } from "react-use-websocket";

import { Button } from "../../Button";
import ActionChain, { ActionChainLink, linkToPredicate } from "./ActionChain";
import { Option } from "../util/constants";

const getArgumentCount = (option: Option) => {
    return Array.from(option.value).filter((str) => str === "?").length;
};

const getArgumentNames = (signature: string) => {
    return signature
        .slice(signature.indexOf("(") + 1, signature.indexOf(")"))
        .split(",")
        .map((arg) => {
            return arg.trim()
                .slice(1)
        });
};

type Props = {
    actionsAvailable: string[],
    sendMessage: SendMessage,
    path: string
};

const ActionBuilder: React.FC<Props> = ({
    actionsAvailable, sendMessage, path
}) => {
    const actionOptions: Option[] = actionsAvailable.map((str) => {
        return { value: str, label: str }
    });

    const [actionName, setActionName] = useState<string>("");
    const handleActionNameChange = (e: React.ChangeEvent<HTMLInputElement>) =>
        setActionName(e.target.value);

    const [actionChain, setActionChain] = useState<ActionChainLink[]>([]);

    const handleChainSelect = (index: number, option: SingleValue<Option>) => {
        if (option === null) return;
        setActionChain(actionChain.slice(0, index)
            .concat({
                action: option,
                args: new Array(getArgumentCount(option)).fill("")
            })
            .concat(actionChain.slice(index + 1)));
    };

    const handleArgInputChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        actionLinkIndex: number,
        argumentIndex: number
    ) => {
        setActionChain(actionChain.slice(0, actionLinkIndex)
            .concat({
                action: actionChain[actionLinkIndex].action,
                args: actionChain[actionLinkIndex].args.slice(0, argumentIndex)
                    .concat(e.target.value)
                    .concat(actionChain[actionLinkIndex].args.slice(argumentIndex + 1))
            })
            .concat(actionChain.slice(actionLinkIndex + 1))
        );
    };

    const addToChain = () => {
        setActionChain(actionChain.concat({
            action: actionOptions[0],
            args: new Array(getArgumentCount(actionOptions[0])).fill("")
        }));
    };

    const deleteFromChain = (index: number) => {
        setActionChain(actionChain.slice(0, index)
            .concat(actionChain.slice(index + 1)));
    };

    const handleSubmit = (e: React.FormEvent) => {
        sendMessage(JSON.stringify({
            path: path,
            method: "builder",
            name: actionName,
            chain: actionChain.map(link => linkToPredicate(link))
        }));

        handleReset(e);
    };

    const handleReset = (e: React.FormEvent) => {
        setActionName("");
        setActionChain([]);
        e.preventDefault();
    };

    return (
        <form
            className="flex flex-col rounded-md shadow-md border
                       border-border grow p-3 gap-2 bg-card"
            onSubmit={handleSubmit}
            onReset={handleReset}>

            <label className="rounded-md shadow-md border border-border
                              px-2 pt-2">
                Action name
                <span className="text-red-500 dark:text-red-400">*</span>
                <div className="flex flex-row items-center gap-1">
                    <input
                        className="block box-border rounded mt-1 mb-2 text-sm
                           border border-input bg-background text-foreground p-2 font-mono grow
                           focus:outline-none focus:ring-2 focus:ring-ring"
                        type="text"
                        value={actionName}
                        onChange={handleActionNameChange}
                        required
                    />
                    <label>(?actor)</label>
                </div>
            </label>

            <ActionChain
                actionChain={actionChain}
                actionOptions={actionOptions}
                handleChainSelect={handleChainSelect}
                deleteFromChain={deleteFromChain}
                handleArgInputChange={handleArgInputChange}
                getArgumentNames={getArgumentNames}
                addToChain={addToChain}
            />

            <div className="grid grid-cols-2 gap-2">
                <Button
                    type="submit"
                    title="Add action">
                    Add
                </Button>
                <Button
                    type="reset"
                    title="Clear action">
                    Clear
                </Button>
            </div>
        </form>
    );
};

export default ActionBuilder;
